import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLeads, getAnfragen } from '../lib/crm-api';
import { getProjects } from '../lib/api';
import { getAufgaben } from '../lib/crm-api';
import { supabase } from '../lib/supabase';
import Select from '../components/ui/Select';

type TimeRange = 'today' | 'week' | 'month' | 'year' | 'custom';

export default function StatistikenPage() {
  const [timeRange, setTimeRange] = useState<TimeRange>('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Data fetching
  const { data: leads = [] } = useQuery({ queryKey: ['crm-leads'], queryFn: getLeads });
  const { data: anfragen = [] } = useQuery({ queryKey: ['crm-anfragen'], queryFn: getAnfragen });
  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: () => getProjects() });
  const { data: aufgaben = [] } = useQuery({ queryKey: ['crm-aufgaben'], queryFn: () => getAufgaben() });
  const { data: formSubmissions = [] } = useQuery({
    queryKey: ['form-submissions'],
    queryFn: async () => {
      const { data, error } = await supabase.from('form_submissions').select('*');
      if (error) throw error;
      return data || [];
    },
  });

  // Calculate date range
  const dateRange = useMemo(() => {
    const now = new Date();
    let start: Date;
    let end: Date = now;

    switch (timeRange) {
      case 'today':
        start = new Date(now.setHours(0, 0, 0, 0));
        break;
      case 'week':
        start = new Date(now.setDate(now.getDate() - 7));
        break;
      case 'month':
        start = new Date(now.setMonth(now.getMonth() - 1));
        break;
      case 'year':
        start = new Date(now.setFullYear(now.getFullYear() - 1));
        break;
      case 'custom':
        start = customStart ? new Date(customStart) : new Date(now.setMonth(now.getMonth() - 1));
        end = customEnd ? new Date(customEnd) : now;
        break;
      default:
        start = new Date(now.setMonth(now.getMonth() - 1));
    }

    return { start: start.toISOString(), end: end.toISOString() };
  }, [timeRange, customStart, customEnd]);

  // Filter data by date range
  const filteredLeads = leads.filter(l => l.created_at >= dateRange.start && l.created_at <= dateRange.end);
  const filteredAnfragen = anfragen.filter(a => a.created_at >= dateRange.start && a.created_at <= dateRange.end);
  const filteredSubmissions = formSubmissions.filter((s: any) => s.submitted_at >= dateRange.start && s.submitted_at <= dateRange.end);

  // Calculate stats
  const stats = {
    leads: {
      total: filteredLeads.length,
      neu: filteredLeads.filter(l => l.status === 'neu').length,
      kontaktiert: filteredLeads.filter(l => l.status === 'kontaktiert').length,
      interessiert: filteredLeads.filter(l => l.status === 'interessiert').length,
      gewonnen: filteredLeads.filter(l => l.status === 'gewonnen').length,
      verloren: filteredLeads.filter(l => l.status === 'nicht_interessiert').length,
    },
    anfragen: {
      total: filteredAnfragen.length,
      neu: filteredAnfragen.filter(a => a.status === 'neu').length,
      // AnfrageStatus kennt kein 'in_bearbeitung'/'angebot_erstellt' — auf echte Pipeline-Stufen mappen
      inBearbeitung: filteredAnfragen.filter(a =>
        a.status === 'erstgespraech_geplant' || a.status === 'angebot_in_arbeit' ||
        a.status === 'verkaufsgespraech' || a.status === 'nachfassen'
      ).length,
      angebotErstellt: filteredAnfragen.filter(a => a.status === 'angebot_versendet').length,
      gewonnen: filteredAnfragen.filter(a => a.status === 'gewonnen').length,
      verloren: filteredAnfragen.filter(a => a.status === 'verloren').length,
    },
    aufgaben: {
      total: aufgaben.length,
      offen: aufgaben.filter(a => !a.erledigt).length,
      erledigt: aufgaben.filter(a => a.erledigt).length,
      faellig: aufgaben.filter(a => !a.erledigt && a.deadline && a.deadline <= new Date().toISOString().split('T')[0]).length,
    },
    projekte: {
      total: projects.length,
      aktiv: projects.filter(p => p.active).length,
    },
    formulare: {
      total: filteredSubmissions.length,
      leadGeneration: filteredSubmissions.filter((s: any) => s.form_type === 'lead_generation').length,
      recruiting: filteredSubmissions.filter((s: any) => s.form_type === 'recruiting').length,
    },
  };

  // Conversion rates
  const leadConversionRate = stats.leads.total > 0 ? (stats.leads.gewonnen / stats.leads.total * 100).toFixed(1) : '0';
  const anfrageSuccessRate = stats.anfragen.total > 0 ? (stats.anfragen.gewonnen / stats.anfragen.total * 100).toFixed(1) : '0';
  const aufgabenCompletionRate = stats.aufgaben.total > 0 ? (stats.aufgaben.erledigt / stats.aufgaben.total * 100).toFixed(1) : '0';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Statistiken</h1>
          <p className="text-sm text-gray-500 mt-0.5">Vertriebskennzahlen & Performance-Übersicht</p>
        </div>
      </div>

      {/* Time Range Filter */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <Select
              label="Zeitraum"
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as TimeRange)}
              options={[
                { value: 'today', label: 'Heute' },
                { value: 'week', label: 'Letzte 7 Tage' },
                { value: 'month', label: 'Letzter Monat' },
                { value: 'year', label: 'Letztes Jahr' },
                { value: 'custom', label: 'Benutzerdefiniert' },
              ]}
            />
          </div>
          {timeRange === 'custom' && (
            <>
              <div className="flex-1 min-w-[150px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Von</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                />
              </div>
              <div className="flex-1 min-w-[150px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Bis</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Lead-Konversion"
          value={`${leadConversionRate}%`}
          subtitle={`${stats.leads.gewonnen} von ${stats.leads.total} Leads`}
          trend={parseFloat(leadConversionRate) >= 20 ? 'up' : parseFloat(leadConversionRate) >= 10 ? 'neutral' : 'down'}
        />
        <MetricCard
          title="Anfragen-Erfolgsquote"
          value={`${anfrageSuccessRate}%`}
          subtitle={`${stats.anfragen.gewonnen} von ${stats.anfragen.total} Anfragen`}
          trend={parseFloat(anfrageSuccessRate) >= 50 ? 'up' : parseFloat(anfrageSuccessRate) >= 30 ? 'neutral' : 'down'}
        />
        <MetricCard
          title="Aufgaben-Completion"
          value={`${aufgabenCompletionRate}%`}
          subtitle={`${stats.aufgaben.erledigt} von ${stats.aufgaben.total} erledigt`}
          trend={parseFloat(aufgabenCompletionRate) >= 70 ? 'up' : parseFloat(aufgabenCompletionRate) >= 50 ? 'neutral' : 'down'}
        />
        <MetricCard
          title="Fällige Aufgaben"
          value={stats.aufgaben.faellig.toString()}
          subtitle={`${stats.aufgaben.offen} offen insgesamt`}
          trend={stats.aufgaben.faellig === 0 ? 'up' : stats.aufgaben.faellig <= 3 ? 'neutral' : 'down'}
          alert={stats.aufgaben.faellig > 0}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lead Status */}
        <ChartCard title="Lead-Status">
          <BarChart
            labels={['Neu', 'Kontaktiert', 'Interessiert', 'Gewonnen', 'Kein Interesse']}
            values={[
              stats.leads.neu,
              stats.leads.kontaktiert,
              stats.leads.interessiert,
              stats.leads.gewonnen,
              stats.leads.verloren,
            ]}
            colors={['#9ca3af', '#3b82f6', '#10b981', '#22c55e', '#ef4444']}
          />
        </ChartCard>

        {/* Anfragen Status */}
        <ChartCard title="Anfragen-Pipeline">
          <BarChart
            labels={['Neu', 'In Bearb.', 'Angebot', 'Gewonnen', 'Verloren']}
            values={[
              stats.anfragen.neu,
              stats.anfragen.inBearbeitung,
              stats.anfragen.angebotErstellt,
              stats.anfragen.gewonnen,
              stats.anfragen.verloren,
            ]}
            colors={['#9ca3af', '#3b82f6', '#8b5cf6', '#22c55e', '#ef4444']}
          />
        </ChartCard>

        {/* Aufgaben Status */}
        <ChartCard title="Aufgaben-Übersicht">
          <PieChart
            segments={[
              { label: 'Offen', value: stats.aufgaben.offen, color: '#3b82f6' },
              { label: 'Erledigt', value: stats.aufgaben.erledigt, color: '#22c55e' },
              { label: 'Fällig', value: stats.aufgaben.faellig, color: '#ef4444' },
            ]}
          />
        </ChartCard>

        {/* Formular-Eingänge */}
        <ChartCard title="Formular-Eingänge">
          <BarChart
            labels={['Lead-Gen', 'Recruiting']}
            values={[stats.formulare.leadGeneration, stats.formulare.recruiting]}
            colors={['#3b82f6', '#8b5cf6']}
          />
        </ChartCard>
      </div>

      {/* Detailed Stats Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <StatsTable title="Leads im Detail" data={[
          { label: 'Gesamt', value: stats.leads.total, color: 'gray' },
          { label: 'Neu', value: stats.leads.neu, color: 'gray' },
          { label: 'Kontaktiert', value: stats.leads.kontaktiert, color: 'blue' },
          { label: 'Interessiert', value: stats.leads.interessiert, color: 'emerald' },
          { label: 'Gewonnen', value: stats.leads.gewonnen, color: 'green' },
          { label: 'Kein Interesse', value: stats.leads.verloren, color: 'red' },
        ]} />

        <StatsTable title="Anfragen im Detail" data={[
          { label: 'Gesamt', value: stats.anfragen.total, color: 'gray' },
          { label: 'Neu', value: stats.anfragen.neu, color: 'gray' },
          { label: 'In Bearbeitung', value: stats.anfragen.inBearbeitung, color: 'blue' },
          { label: 'Angebot erstellt', value: stats.anfragen.angebotErstellt, color: 'purple' },
          { label: 'Gewonnen', value: stats.anfragen.gewonnen, color: 'green' },
          { label: 'Verloren', value: stats.anfragen.verloren, color: 'red' },
        ]} />
      </div>
    </div>
  );
}

// =============================================
// COMPONENTS
// =============================================

function MetricCard({ 
  title, 
  value, 
  subtitle, 
  trend, 
  alert 
}: { 
  title: string; 
  value: string; 
  subtitle: string; 
  trend: 'up' | 'down' | 'neutral';
  alert?: boolean;
}) {
  const trendColors = {
    up: 'text-green-600',
    down: 'text-red-600',
    neutral: 'text-gray-600',
  };

  const trendIcons = {
    up: '↗',
    down: '↘',
    neutral: '→',
  };

  return (
    <div className={`bg-white border rounded-lg p-4 ${alert ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{title}</p>
      <div className="flex items-baseline gap-2">
        <p className={`text-2xl font-bold ${alert ? 'text-red-700' : 'text-gray-900'}`}>{value}</p>
        <span className={`text-sm ${trendColors[trend]}`}>{trendIcons[trend]}</span>
      </div>
      <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">{title}</h3>
      {children}
    </div>
  );
}

function BarChart({ labels, values, colors }: { labels: string[]; values: number[]; colors: string[] }) {
  const max = Math.max(...values, 1);

  return (
    <div className="space-y-3">
      {labels.map((label, idx) => (
        <div key={idx}>
          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
            <span>{label}</span>
            <span className="font-medium">{values[idx]}</span>
          </div>
          <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full transition-all duration-500 flex items-center justify-end pr-2"
              style={{
                width: `${(values[idx] / max) * 100}%`,
                backgroundColor: colors[idx],
              }}
            >
              {values[idx] > 0 && (
                <span className="text-xs font-medium text-white">{values[idx]}</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PieChart({ segments }: { segments: { label: string; value: number; color: string }[] }) {
  const total = segments.reduce((sum, seg) => sum + seg.value, 0);

  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
        Keine Daten verfügbar
      </div>
    );
  }

  return (
    <div className="flex items-center gap-6">
      {/* Legend */}
      <div className="flex-1 space-y-2">
        {segments.map((seg, idx) => {
          const percentage = ((seg.value / total) * 100).toFixed(1);
          return (
            <div key={idx} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: seg.color }}
                />
                <span className="text-sm text-gray-700">{seg.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">{seg.value}</span>
                <span className="text-xs text-gray-500">({percentage}%)</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Simple Donut Visualization */}
      <div className="relative w-32 h-32 flex-shrink-0">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          {segments.map((seg, idx) => {
            const prevSum = segments.slice(0, idx).reduce((sum, s) => sum + s.value, 0);
            const offset = (prevSum / total) * 283; // 283 ≈ 2πr for r=45
            const length = (seg.value / total) * 283;

            return (
              <circle
                key={idx}
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke={seg.color}
                strokeWidth="10"
                strokeDasharray={`${length} 283`}
                strokeDashoffset={-offset}
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{total}</p>
            <p className="text-xs text-gray-500">Gesamt</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatsTable({ title, data }: { title: string; data: { label: string; value: number; color: string }[] }) {
  const colorClasses: Record<string, string> = {
    gray: 'text-gray-600',
    blue: 'text-blue-600',
    emerald: 'text-emerald-600',
    green: 'text-green-600',
    red: 'text-red-600',
    purple: 'text-purple-600',
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="space-y-2">
        {data.map((row, idx) => (
          <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
            <span className="text-sm text-gray-700">{row.label}</span>
            <span className={`text-sm font-semibold ${colorClasses[row.color] || 'text-gray-900'}`}>
              {row.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
